import { useState } from 'react'
import { useForm } from 'react-hook-form'
import { toast } from 'sonner'
import { Button } from '@/components/shadcn/button'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from '@/components/shadcn/dialog'
import { Form } from '@/components/shadcn/form'
import { Input } from '@/components/shadcn/input'
import { Tooltip, TooltipContent, TooltipTrigger } from '@/components/shadcn/tooltip'
import { FileUploadField } from '@/components/form-components/FileUploadField'
import { ConfirmDialog } from '@/components/ui/ConfirmDialog'
import { useAppContext } from '@/catalog-apps/hooks/useAppContext'
import { queryClient, useRestClient } from '@/contexts/QueryProvider'
import { IconV2FolderOutline } from '@icons/IconV2FolderOutline'
import { IconV2DownloadOutline } from '@icons/IconV2DownloadOutline'
import { IconV2TrashOutline } from '@icons/IconV2TrashOutline'
import { IconV2UploadOutline } from '@icons/IconV2UploadOutline'
import { ENDPOINTS } from '@/util'
import { getFiles, joinNamespace } from './fileManagerTreeUtils'
import type { LandingFile, UploadFilesFormValues } from './fileManagerTreeTypes'

export function FileManagerCreateFolderDialog({
  parentNamespace,
  refreshNamespace,
  triggerTooltip = 'New folder',
  triggerAriaLabel = 'New folder',
  triggerIconClassName = 'size-6',
}: {
  parentNamespace: string
  refreshNamespace: string
  triggerTooltip?: string
  triggerAriaLabel?: string
  triggerIconClassName?: string
}) {
  const [isOpen, setIsOpen] = useState(false)
  const [folderName, setFolderName] = useState('')
  const { postStorageApiCreateLandingDirectory } = useRestClient()
  const { appKey, projectId, organisationId } = useAppContext()
  const { mutate: createFolder, isPending: isCreatingFolder } = postStorageApiCreateLandingDirectory.useMutation(
    undefined,
    {
      onSuccess: () => {
        queryClient.invalidateQueries({
          queryKey: ['listLandingFiles', appKey, projectId, organisationId, refreshNamespace],
        })
        toast.success('Folder created')
        setFolderName('')
        setIsOpen(false)
      },
      onError: (error) => {
        console.error('Error creating folder:', error)
        toast.error('Failed to create folder')
      },
    }
  )

  const trimmedFolderName = folderName.trim()

  const onCreate = () => {
    if (!trimmedFolderName) {
      toast.error('Please enter a folder name')
      return
    }

    if (!projectId || projectId < 0 || !appKey) {
      toast.error('Missing app or project context for folder creation')
      return
    }

    createFolder({
      body: {
        appKey,
        projectId,
        namespace: joinNamespace(parentNamespace, trimmedFolderName),
      },
    })
  }

  const onOpenChange = (open: boolean) => {
    setIsOpen(open)

    if (!open) {
      setFolderName('')
    }
  }

  return (
    <Dialog open={isOpen} onOpenChange={onOpenChange}>
      <Tooltip>
        <TooltipTrigger asChild>
          <DialogTrigger asChild>
            <Button type="button" variant="ghost" size="icon" aria-label={triggerAriaLabel}>
              <IconV2FolderOutline className={triggerIconClassName} />
            </Button>
          </DialogTrigger>
        </TooltipTrigger>
        <TooltipContent side="top" align="center" sideOffset={6}>
          {triggerTooltip}
        </TooltipContent>
      </Tooltip>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>New folder</DialogTitle>
          <DialogDescription>Enter a folder name to create a new folder.</DialogDescription>
        </DialogHeader>
        <div className="space-y-2">
          <label className="text-sm font-medium" htmlFor="file-manager-folder-name">
            Folder name
          </label>
          <Input
            id="file-manager-folder-name"
            value={folderName}
            placeholder="Folder name"
            autoFocus
            onChange={(event) => setFolderName(event.target.value)}
            onKeyDown={(event) => {
              if (event.key === 'Enter') {
                event.preventDefault()
                onCreate()
              }
            }}
          />
        </div>
        <DialogFooter>
          <Button type="button" variant="outline" onClick={() => onOpenChange(false)} disabled={isCreatingFolder}>
            Cancel
          </Button>
          <Button type="button" onClick={onCreate} disabled={!trimmedFolderName || isCreatingFolder}>
            {isCreatingFolder ? 'Creating...' : 'Create'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}

export function FileManagerDeleteFileAction({
  file,
  namespace,
  deleteNamespace,
  description,
  successMessage = 'File deleted successfully',
  errorMessage = 'Failed to delete file',
}: {
  file: LandingFile
  namespace: string
  deleteNamespace?: string
  description?: string
  successMessage?: string
  errorMessage?: string
}) {
  const { deleteStorageApiDeleteLandingFile } = useRestClient()
  const { appKey, projectId, organisationId } = useAppContext()
  const { mutate: deleteFile, isPending: isDeleting } = deleteStorageApiDeleteLandingFile.useMutation(undefined, {
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: ['listLandingFiles', appKey, projectId, organisationId, namespace],
      })
      toast.success(successMessage)
    },
    onError: (error) => {
      console.error('Error deleting file:', error)
      toast.error(errorMessage)
    },
  })

  const onDelete = () => {
    if (!appKey || !projectId || projectId < 0 || organisationId == null) {
      toast.error('Missing app, project, or organisation context for file deletion')
      return
    }

    if (!file.fileName) {
      toast.error('Missing filename for file deletion')
      return
    }

    deleteFile({
      body: {
        appKey,
        projectId,
        organisationId,
        namespace: deleteNamespace ?? file.namespace ?? namespace,
        filename: file.fileName,
      },
    })
  }

  return (
    <div className="flex justify-end">
      <ConfirmDialog
        triggerTooltip="Delete"
        triggerTooltipSide="top"
        trigger={
          <Button
            type="button"
            variant="ghost"
            size="icon"
            className="text-muted-foreground hover:text-destructive"
            disabled={isDeleting}
            aria-label="Delete file"
          >
            <IconV2TrashOutline className="size-5" />
          </Button>
        }
        title="Delete file"
        description={description ?? `Are you sure you want to delete ${file.fileName || 'this file'}?`}
        confirmLabel="Delete"
        confirmVariant="destructive"
        onConfirm={onDelete}
        isSubmitting={isDeleting}
      />
    </div>
  )
}

export function FileManagerDownloadFileAction({ file }: { file: LandingFile }) {
  const { appKey, projectId } = useAppContext()
  const [isDownloading, setIsDownloading] = useState(false)

  const onDownload = async () => {
    if (!file.encryptedBlobName) {
      toast.error('Missing file reference for download')
      return
    }

    if (!projectId || projectId < 0 || !appKey) {
      toast.error('Missing app or project context for file download')
      return
    }

    setIsDownloading(true)

    try {
      const token = JSON.parse(sessionStorage.getItem('token') ?? '')
      const params = new URLSearchParams()
      params.set('encryptedBlobName', file.encryptedBlobName)
      params.set('appKey', appKey)
      params.set('projectId', projectId.toString())

      const response = await fetch(`${ENDPOINTS.LANDING}/storage/download_landing_file/?${params.toString()}`, {
        headers: {
          Authorization: 'Bearer ' + token,
        },
      })

      if (!response.ok) {
        let errorMessage = response.statusText || 'Unknown error'

        try {
          const responseJson = await response.json()
          if (responseJson.Message) {
            errorMessage = responseJson.Message
          }
        } catch {
          // Keep the status text when the error response is not JSON.
        }

        toast.error(`Error downloading file: ${errorMessage}`)
        return
      }

      const blob = await response.blob()
      const url = window.URL.createObjectURL(blob)
      const link = document.createElement('a')
      link.href = url
      link.download = file.fileName ?? 'download'
      document.body.appendChild(link)
      link.click()
      link.remove()
      window.URL.revokeObjectURL(url)
    } catch (error) {
      console.error('Error downloading file:', error)
      toast.error('Error downloading file')
    } finally {
      setIsDownloading(false)
    }
  }

  return (
    <Tooltip>
      <TooltipTrigger asChild>
        <Button
          type="button"
          variant="ghost"
          size="icon"
          className="text-muted-foreground hover:text-foreground"
          disabled={isDownloading}
          aria-label="Download file"
          onClick={onDownload}
        >
          <IconV2DownloadOutline className="size-5" />
        </Button>
      </TooltipTrigger>
      <TooltipContent side="top" align="center" sideOffset={6}>
        Download
      </TooltipContent>
    </Tooltip>
  )
}

export function FileManagerUploadDialog({
  uploadNamespace,
  refreshNamespace,
  acceptedFormats,
  triggerLabel,
  triggerTooltip = 'Upload files',
  triggerClassName,
  maxFileSizeMb,
}: {
  uploadNamespace: string
  refreshNamespace: string
  acceptedFormats?: `.${string}`[]
  triggerLabel?: string
  triggerTooltip?: string
  triggerClassName?: string
  maxFileSizeMb?: number
}) {
  const [isOpen, setIsOpen] = useState(false)
  const { postStorageApiUploadLandingFiles } = useRestClient()
  const { appKey, projectId, organisationId } = useAppContext()
  const form = useForm<UploadFilesFormValues>({
    defaultValues: {
      files: [],
    },
  })
  const { mutate: uploadFiles, isPending: isUploading } = postStorageApiUploadLandingFiles.useMutation(undefined, {
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: ['listLandingFiles', appKey, projectId, organisationId, refreshNamespace],
      })
      toast.success('File upload completed')
      form.reset({ files: [] })
      setIsOpen(false)
    },
    onError: (error) => {
      console.error('Error uploading file(s):', error)
      toast.error('File upload failed')
    },
  })

  const selectedFiles = form.watch('files')
  const fileCount = getFiles(selectedFiles).length

  const onSubmit = (data: UploadFilesFormValues) => {
    const files = getFiles(data.files)

    if (!files.length) {
      toast.error('Please select at least one file')
      return
    }

    if (!projectId || projectId < 0 || !appKey) {
      toast.error('Missing app or project context for file upload')
      return
    }

    const formData = new FormData()
    formData.append('projectId', projectId.toString())
    formData.append('appKey', appKey)
    formData.append('namespace', uploadNamespace)
    files.forEach((file, index) => {
      formData.append(`file${index + 1}`, file, file.name)
    })

    uploadFiles({ body: formData })
  }

  return (
    <Dialog open={isOpen} onOpenChange={setIsOpen}>
      <Tooltip>
        <TooltipTrigger asChild>
          <DialogTrigger asChild>
            <Button
              type="button"
              variant="ghost"
              size={triggerLabel ? 'sm' : 'icon'}
              className={triggerClassName}
              aria-label={triggerLabel ?? 'Upload files'}
            >
              <IconV2UploadOutline className={triggerLabel ? 'size-4' : 'size-6'} />
              {triggerLabel && <span>{triggerLabel}</span>}
            </Button>
          </DialogTrigger>
        </TooltipTrigger>
        <TooltipContent side="top" align="center" sideOffset={6}>
          {triggerTooltip}
        </TooltipContent>
      </Tooltip>
      <DialogContent className="max-w-2xl">
        <DialogHeader>
          <DialogTitle>Upload files</DialogTitle>
          <DialogDescription>Select one or more files to upload to this file manager.</DialogDescription>
        </DialogHeader>
        <Form {...form}>
          <form className="space-y-6" onSubmit={form.handleSubmit(onSubmit)}>
            <FileUploadField
              control={form.control}
              name="files"
              label="Files"
              multiple
              maxFiles={20}
              acceptedFormats={acceptedFormats}
              maxSize={maxFileSizeMb ? maxFileSizeMb * 1024 * 1024 : undefined}
            />
            <DialogFooter>
              <Button type="submit" disabled={!fileCount || isUploading}>
                {isUploading ? 'Uploading...' : 'Upload'}
              </Button>
            </DialogFooter>
          </form>
        </Form>
      </DialogContent>
    </Dialog>
  )
}
